'use strict';

/**
 * Auto-rotate carousel — play/pause control, slide a11y, and hover awareness.
 *
 * Reads its config from an application/json script tag (linked via
 * data-config-for). Manages autoplay with a delayed start, play/pause toggle,
 * slide accessibility (aria-hidden, tabindex on links), and hover-based
 * autoplay pausing via the 'globalPromoBanner:hoverChange' CustomEvent
 * dispatched by global-promo-banner.js.
 *
 * @see global-promo-banner.js — dispatches hover events consumed here
 * @see slick-carousel-init.js — initializes Slick before this script runs
 */

/**
 * Syncs play/pause button icon, title, aria-label, and aria-pressed.
 * @param {{
 * isAutoplay: boolean,
 * pauseIcon: Element|null,
 * playIcon: Element|null,
 * playPauseBtn: HTMLButtonElement,
 * pauseText: string,
 * playText: string
 * }} ctx - Carousel control context.
 * @private
 */
function _updateIcons(ctx) {
    if (ctx.pauseIcon) {
        ctx.pauseIcon.style.display = ctx.isAutoplay ? '' : 'none';
    }
    if (ctx.playIcon) {
        ctx.playIcon.style.display = ctx.isAutoplay ? 'none' : '';
    }

    let label = ctx.isAutoplay ? ctx.pauseText : ctx.playText;
    ctx.playPauseBtn.setAttribute('title', label);
    ctx.playPauseBtn.setAttribute('aria-label', label);
    ctx.playPauseBtn.setAttribute('aria-pressed', String(ctx.isAutoplay));
    let srSpan = ctx.playPauseBtn.querySelector('.visually-hidden');
    if (srSpan) srSpan.textContent = label;
}

/**
 * Updates aria-hidden on slides and link tabindex for the active slide.
 * @param {JQuery} $carousel - Slick carousel jQuery wrapper.
 * @private
 */
function _updateSlideA11y($carousel) {
    let currentSlide = $carousel.slick('slickCurrentSlide');
    let slides = $carousel[0].querySelectorAll('.autoRotateCarousel__text');

    for (let i = 0; i < slides.length; i++) {
        let slickSlide = slides[i].closest('.slick-slide');
        let slideIndex = parseInt(
            slickSlide && slickSlide.getAttribute('data-slick-index'),
            10,
        );
        let isActive = !isNaN(slideIndex) && slideIndex === currentSlide;

        slides[i].setAttribute('aria-hidden', String(!isActive));
        if (slickSlide) slickSlide.removeAttribute('tabindex');

        let link = slides[i].querySelector('.autoRotateCarousel__link');

        if (link) {
            link.setAttribute('tabindex', isActive ? '0' : '-1');
        }
    }
}

/**
 * Creates a MutationObserver that strips Slick-assigned slide tabindex values.
 * @param {JQuery} _$carousel - Slick carousel jQuery wrapper.
 * @returns {MutationObserver}
 * @private
 */
function _createTabindexObserver(_$carousel) {
    return new MutationObserver((mutations) => {
        for (let j = 0; j < mutations.length; j++) {
            let target = mutations[j].target;

            if (
                mutations[j].attributeName === 'tabindex' &&
                target.classList.contains('slick-slide') &&
                target.closest('.autoRotateCarousel__slick')
            ) {
                target.removeAttribute('tabindex');
            }
        }
    });
}

/**
 * Toggles hover-visible classes and pauses/resumes autoplay on banner hover.
 * @param {HTMLElement} root - Auto-rotate carousel root element.
 * @param {JQuery} $carousel - Slick carousel jQuery wrapper.
 * @param {{ isAutoplay: boolean }} state - Mutable autoplay state.
 * @param {CustomEvent} e - Hover change event from the promo banner.
 * @private
 */
function _onBannerHoverChange(root, $carousel, state, e) {
    let isHovered = e.detail && e.detail.isHovered;

    let controls = root.querySelectorAll(
        '.autoRotateCarousel__playPauseBtn, .autoRotateCarousel__navigationButton',
    );

    for (let i = 0; i < controls.length; i++) {
        let baseClass = controls[i].classList.contains(
            'autoRotateCarousel__playPauseBtn',
        )
            ? 'autoRotateCarousel__playPauseBtn--hovered'
            : 'autoRotateCarousel__navigationButton--hovered';

        if (isHovered) {
            controls[i].classList.add(baseClass);
        } else {
            controls[i].classList.remove(baseClass);
        }
    }

    if (state.isAutoplay) {
        if (isHovered) {
            $carousel.slick('slickPause');
        } else {
            $carousel.slick('slickPlay');
        }
    }
}

/**
 * Initializes auto-rotate carousel controls when markup and config are present.
 */
export function initAutoRotateCarousel() {
    let root = document.querySelector('.autoRotateCarousel');
    if (!root) return;

    let configEl = document.querySelector(`[data-config-for="${root.id}"]`);
    if (!configEl) return;

    let config;
    try {
        config = JSON.parse(configEl.textContent);
    } catch {
        return;
    }

    let rootId = root.id;
    let $carousel = jQuery(`#${config.slickId}`);
    let playPauseBtn = document.getElementById(`${rootId}-play-pause-btn`);

    if (!$carousel.length || !playPauseBtn) return;

    let pauseText = config.pauseText || 'Pause';
    let playText = config.playText || 'Play';
    let state = { isAutoplay: true };

    let pauseIcon = playPauseBtn.querySelector(
        '.autoRotateCarousel__icon--pause',
    );
    let playIcon = playPauseBtn.querySelector(
        '.autoRotateCarousel__icon--play',
    );

    let autoplaySpeed = config.autoplaySpeed || 5000;

    setTimeout(() => {
        if (state.isAutoplay) {
            $carousel.slick('slickPlay');
        }
    }, autoplaySpeed);

    let iconContext = {
        isAutoplay: state.isAutoplay,
        pauseIcon: pauseIcon,
        playIcon: playIcon,
        playPauseBtn: playPauseBtn,
        pauseText: pauseText,
        playText: playText,
    };

    playPauseBtn.addEventListener('click', (e) => {
        if (!state.isAutoplay) {
            $carousel.slick('slickNext');
        }

        state.isAutoplay = !state.isAutoplay;
        iconContext.isAutoplay = state.isAutoplay;
        _updateIcons(iconContext);

        if (state.isAutoplay) {
            $carousel.slick('slickPlay');
        } else {
            $carousel.slick('slickPause');
        }

        if (e.detail > 0) {
            playPauseBtn.blur();
        }
    });

    root.addEventListener('click', (e) => {
        let navBtn = e.target.closest('.autoRotateCarousel__navigationButton');
        if (navBtn && e.detail > 0) {
            navBtn.blur();
        }
    });

    let observer = _createTabindexObserver($carousel);

    observer.observe($carousel[0], {
        attributes: true,
        attributeFilter: ['tabindex'],
        subtree: true,
    });

    _updateSlideA11y($carousel);

    $carousel.on('afterChange', () => {
        _updateSlideA11y($carousel);
    });

    let bannerRoot = root.closest('.globalPromoBanner') || root;
    bannerRoot.addEventListener('globalPromoBanner:hoverChange', (e) => {
        _onBannerHoverChange(root, $carousel, state, e);
    });
}
